/*******************************************************************************
 * Copyright (c) 2015 EclipseSource.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ******************************************************************************/

package uk.co.nickthecoder.kojak

/**
 * Represents a JSON value. This can be a JSON _object_, an _array_,
 * a _number_, a _string_, or one of the literals
 * _true_, _false_, and _null_.
 *
 * The literals _true_, _false_, and _null_ are
 * represented by the constants {@link Json#TRUE}, {@link Json#FALSE}, and {@link Json#NULL}.
 *
 * JSON _objects_ and _arrays_ are represented by the subtypes
 * [JsonObject] and [JsonArray]. Instances of these types can be created using the
 * public constructors of these classes.
 *
 * Instances that represent JSON _numbers_, _strings_ and
 * _boolean_ values can be created using the static factory methods
 * [Json.value].
 *
 * In order to find out whether an instance of this class is of a certain type, the methods
 * [isObject], [isArray], [isString], [isNumber] etc. can be used.
 *
 * If the JSON value's type is known, the methods [asObject], [asArray],
 * [asString], [asInt], etc. can be used to get this value directly in the
 * appropriate target type.
 *
 */
abstract class JsonValue {

    /**
     * Detects whether this value represents a JSON object. If this is the case, this value is an
     * instance of {@link JsonObject}.
     *
     * @return `true` if this value is an instance of JsonObject
     */
    open fun isObject(): Boolean = false

    /**
     * Detects whether this value represents a JSON array. If this is the case, this value is an
     * instance of {@link JsonArray}.
     *
     * @return `true` if this value is an instance of JsonArray
     */
    open fun isArray() = false

    /**
     * Detects whether this value represents a JSON number.
     *
     * @return `true` if this value represents a JSON number
     */
    open fun isNumber() = false

    /**
     * Detects whether this value represents a JSON string.
     *
     * @return `true` if this value represents a JSON string
     */
    open fun isString() = false

    /**
     * Detects whether this value represents a boolean value.
     *
     * @return `true` if this value represents either the JSON literal `true` or `false`
     */
    open fun isBoolean() = false

    /**
     * Detects whether this value represents the JSON literal `true`.
     *
     * @return `true` if this value represents the JSON literal `true`
     */
    open fun isTrue() = false

    /**
     * Detects whether this value represents the JSON literal `false`.
     *
     * @return `true` if this value represents the JSON literal `false`
     */
    open fun isFalse() = false

    /**
     * Detects whether this value represents the JSON literal `null`.
     *
     * @return `true` if this value represents the JSON literal `null`
     */
    open fun isNull() = false

    /**
     * Returns this JSON value as [JsonObject], assuming that this value represents a JSON
     * object. If this is not the case, an exception is thrown.
     *
     * @return a JSONObject for this value
     * @throws UnsupportedOperationException if this value is not a JSON object
     */
    open fun asObject(): JsonObject {
        throw UnsupportedOperationException("Not an object: " + toString())
    }

    /**
     * Returns this JSON value as [JsonArray], assuming that this value represents a JSON array.
     * If this is not the case, an exception is thrown.
     *
     * @return a JSONArray for this value
     * @throws UnsupportedOperationException if this value is not a JSON array
     */
    open fun asArray(): JsonArray {
        throw UnsupportedOperationException("Not an array: " + toString())
    }

    /**
     * Returns this JSON value as an `int` value, assuming that this value represents a
     * JSON number that can be interpreted as an [Int]. If this is not the case, an
     * exception is thrown.
     *
     * To be interpreted as an `int`, the JSON number must neither contain an exponent
     * nor a fraction part. Moreover, the number must be in the `Int` range.
     *
     * @return this value as `int` if this value is not a JSON number
     * @throws NumberFormatException if this JSON number can not be interpreted as [Int] value
     */
    open fun asInt(): Int {
        throw UnsupportedOperationException("Not a number: " + toString())
    }

    /**
     * Returns this JSON value as a [Long] value, assuming that this value represents a
     * JSON number that can be interpreted as Java `long`. If this is not the case, an
     * exception is thrown.
     *
     * To be interpreted as Java `long`, the JSON number must neither contain an exponent
     * nor a fraction part. Moreover, the number must be in the [Long] range.
     *
     * @return this value as `long`
     * @throws UnsupportedOperationException if this value is not a JSON number
     * @throws NumberFormatException if this JSON number can not be interpreted as a [Long] value
     */
    open fun asLong(): Long {
        throw UnsupportedOperationException("Not a number: " + toString())
    }

    /**
     * Returns this JSON value as a `float` value, assuming that this value represents a
     * JSON number. If this is not the case, an exception is thrown.
     *
     * If the JSON number is out of the [Float] range, [Float.POSITIVE_INFINITY] or
     * [Float.NEGATIVE_INFINITY] is returned.
     *
     *
     * @return this value as `Float`
     * @throws UnsupportedOperationException if this value is not a JSON number
     */
    open fun asFloat(): Float {
        throw UnsupportedOperationException("Not a number: " + toString())
    }

    /**
     * Returns this JSON value as a `double` value, assuming that this value represents a
     * JSON number. If this is not the case, an exception is thrown.
     *
     * If the JSON number is out of the [Double]` range, [Double.POSITIVE_INFINITY] or
     * [Double.NEGATIVE_INFINITY] is returned.
     *
     *
     * @return this value as `double`
     * @throws UnsupportedOperationException if this value is not a JSON number
     */
    open fun asDouble(): Double {
        throw UnsupportedOperationException("Not a number: " + toString())
    }

    /**
     * Returns this JSON value as String, assuming that this value represents a JSON string. If this
     * is not the case, an exception is thrown.
     *
     * @return the string represented by this value
     * @throws UnsupportedOperationException if this value is not a JSON string
     */
    open fun asString(): String {
        throw UnsupportedOperationException("Not a string: " + toString())
    }

    /**
     * Returns this JSON value as a `boolean` value, assuming that this value is either
     * `true` or `false`. If this is not the case, an exception is thrown.
     *
     * @return this value as `boolean`
     * @throws UnsupportedOperationException if this value is neither `true` or `false`
     */
    open fun asBoolean(): Boolean {
        throw UnsupportedOperationException("Not a boolean: " + toString())
    }

    /**
     * Writes the JSON representation of this value to the given writer using the given formatting.
     * The [writer] is flushed, but not closed.
     *
     * @param writer the writer to write this value to
     * @param config a configuration that controls the formatting or `null` for the minimal form
     */
    open fun writeTo(writer: MPPWriter, config: WriterConfig = WriterConfig.MINIMAL) {
        write(config.createWriter(writer))
        writer.flush()
    }

    /**
     * Returns the JSON string for this value using the given formatting.
     *
     * @param config a configuration that controls the formatting or `null` for the minimal form
     * @return a JSON string that represents this value
     */
    fun toString(config: WriterConfig = WriterConfig.MINIMAL): String {
        val buffer = MPPStringWriter()
        writeTo(buffer, config)
        return buffer.toString()
    }

    override fun toString() = toString(WriterConfig.MINIMAL)

    /**
     * Indicates whether some other object is "equal to" this one according to the contract specified
     * in {@link Object#equals(Object)}.
     *
     * Two JsonValues are considered equal if and only if they represent the same JSON text. As a
     * consequence, two given JsonObjects may be different even though they contain the same set of
     * names with the same values, but in a different order.
     *
     * @param object the reference object with which to compare
     * @return true if this object is the same as the object argument; false otherwise
     */
    override fun equals(other: Any?): Boolean {
        return super.equals(other)
    }

    abstract fun write(writer: JsonWriter)

}
